/* eslint-disable jsdoc/no-types -- Introduced in TP-9489. */

import Message from '../Message/Message';
import ProgressBar from '../ProgressBar/ProgressBar';

import RequestError from './RequestError';
import AbortError from './AbortError';

type ShowErrorToastResolver = (status: number) => boolean;
type GetErrorMessage<TErrorData> = (data: TErrorData | string) => string;

type Data = Document | XMLHttpRequestBodyInit;

const DEFAULT_OPTIONS = {
	csrfToken: true,
	json: true,
	loader: true,
	showErrorToast: () => true,
};

// Top horizontal line loader
const progressBar = new ProgressBar();
// Toasts
const messageController = new Message();

/**
 * Naformátuje data pro payload.
 *
 * @param   {object} data
 * @returns {string}
 */
function _formatFormData(data: Data) {
	return Object.entries(data)
		.map(
			([key, value]) =>
				// eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Introduced in TP-9489.
				`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
		)
		.join('&');
}

interface RequestOptions<TErrorData> {
	abortSignal?: AbortSignal;
	csrfToken?: boolean; // Send CSRF token in header.
	json?: boolean; // Input data are serialized JSON, send them as JSON request.
	loader?: boolean; // Show progress loader.
	showErrorToast?: ShowErrorToastResolver; // Resolver which returns boolean, decides if error toast will be showed in case of error.
	getErrorMessage?: GetErrorMessage<TErrorData>; // Function to get message in case of error.
}

interface RequestParameter<TErrorData> {
	url: string;
	method: 'GET' | 'POST' | 'PUT';
	data?: Data;
	options?: RequestOptions<TErrorData>;
}

type ErrorData = Record<string, unknown>;

/**
 * Common module for all API calls.
 */

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- Introduced in TP-10066.
function _request<TData, TErrorData extends ErrorData>({
	url,
	method,
	data,
	options = DEFAULT_OPTIONS,
}: RequestParameter<TErrorData>): Promise<{ status: number; data: TData }> {
	// Assign defaults
	const {
		abortSignal,
		loader,
		json,
		csrfToken,
		showErrorToast,
		getErrorMessage,
	} = {
		...DEFAULT_OPTIONS,
		...options,
	};

	if (loader) {
		progressBar.start(url);
	}

	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();

		request.open(method, url, true);

		if (csrfToken && window.SSP.csrf_token) {
			request.setRequestHeader('X-CSRFToken', window.SSP.csrf_token);
		}

		function onLoad() {
			const { status } = request;

			if (loader) {
				progressBar.complete(url);
			}

			if (status >= 200 && status < 300) {
				resolve({
					status,
					data:
						typeof request.response === 'string' && request.response
							? (JSON.parse(request.response) as TData)
							: ({} as TData),
				});

				return;
			}

			// ----- Error processing

			let parsedErrorResponse: TErrorData | undefined;
			let errorMessage = 'Request error';

			try {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Introduced in TP-9489.
				parsedErrorResponse = JSON.parse(request.response) as TErrorData;

				if (getErrorMessage) {
					errorMessage = getErrorMessage(parsedErrorResponse);
				} else if (
					'message' in parsedErrorResponse &&
					typeof parsedErrorResponse['message'] === 'string'
				) {
					errorMessage = parsedErrorResponse['message'];
				}
			} catch {
				console.error(
					'Invalid parsing JSON response, received data: ',
					request.response,
				);

				errorMessage =
					getErrorMessage?.(request.statusText) ?? request.statusText;
			}

			if (showErrorToast(status)) {
				messageController.show('warning', errorMessage);
			}

			const error = new RequestError<TErrorData>({
				status,
				message: errorMessage,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Introduced in TP-9489.
				data: request.response,
			});

			reject(error);
		}

		function onError() {
			reject(new Error('Network error'));

			if (loader) {
				progressBar.complete(url);
			}
		}

		request.onload = onLoad;
		request.onerror = onError;

		abortSignal?.addEventListener('abort', () => {
			progressBar.complete(url);
			request.abort();
			reject(new AbortError());
		});

		if (['POST', 'PUT'].includes(method) && data) {
			if (json) {
				request.setRequestHeader(
					'Content-type',
					'application/json;charset=UTF-8',
				);

				request.send(data);
			} else {
				if (data instanceof FormData) {
					request.send(data);
				} else {
					request.setRequestHeader(
						'Content-type',
						'application/x-www-form-urlencoded',
					);

					request.send(_formatFormData(data));
				}
			}

			return;
		}

		request.send();
	});
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unnecessary-type-parameters -- Introduced in TP-9489 and TP-10066.
export function POST<TData, TErrorData extends ErrorData = {}>(
	url: string,
	data?: Data,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		url,
		method: 'POST',
		...(data !== undefined && { data }),
		...(options && { options }),
	});
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unnecessary-type-parameters -- Introduced in TP-9489 and TP-10066.
export function GET<TData, TErrorData extends ErrorData = {}>(
	url: string,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		url,
		method: 'GET',
		...(options && { options }),
	});
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unnecessary-type-parameters -- Introduced in TP-9489 and TP-10066.
export function PUT<TData, TErrorData extends ErrorData = {}>(
	url: string,
	data?: Data,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		url,
		method: 'PUT',
		...(data !== undefined && { data }),
		...(options && { options }),
	});
}
